import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { isAuthenticated, getUser } from './utils/auth';

import HomePage from './pages/HomePage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import LibraryPage from './pages/LibraryPage';
import CheckoutPage from './pages/CheckoutPage';
import SellerDashboard from './pages/SellerDashboard';
import AdminPage from './pages/AdminPage';
import StorePage from './pages/StorePage';
import SellerProfilePage from './pages/SellerProfilePage';
import ProfilePage from './pages/ProfilePage';
import CertificatePage from './pages/CertificatePage';
import AffiliatePage from './pages/AffiliatePage';
import AffiliateDashboardPage from './pages/AffiliateDashboardPage';
import BottomNav from './components/common/BottomNav';

// Redirect /ebook/<slug>-<id> → /?share=<id> so HomePage modal opens
function EbookRedirect() {
  const { ebookId } = useParams();
  const id = ebookId?.length === 24 ? ebookId : ebookId?.split('-').pop();
  return <Navigate to={`/?share=${id}`} replace />;
}

function RequireAuth({ children }) {
  const location = useLocation();
  if (!isAuthenticated()) return <Navigate to="/login" state={{ from: location }} replace />;
  return children;
}

// 'superadmin' role: exact match only
// 'seller' role: seller or user both allowed
// 'user' role: treated as seller+buyer (full access)
function RequireRole({ role, children }) {
  const location = useLocation();
  if (!isAuthenticated()) return <Navigate to="/login" state={{ from: location }} replace />;
  const user = getUser();
  const userRole = user?.role;
  if (role === 'superadmin' && userRole !== 'superadmin') return <Navigate to="/" replace />;
  if (role === 'seller' && userRole !== 'seller' && userRole !== 'user' && userRole !== 'superadmin')
    return <Navigate to="/" replace />;
  return children;
}

export default function App() {
  return (
    <BrowserRouter>
      <Toaster
        position="top-right"
        toastOptions={{
          style: { fontSize: '14px' },
          success: { iconTheme: { primary: '#25D366', secondary: '#fff' } },
        }}
      />
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/" element={<HomePage />} />
        <Route path="/library" element={<RequireAuth><LibraryPage /></RequireAuth>} />
        <Route path="/checkout/:orderId" element={<RequireAuth><CheckoutPage /></RequireAuth>} />
        <Route path="/seller" element={<RequireRole role="seller"><SellerDashboard /></RequireRole>} />
        <Route path="/admin" element={<RequireRole role="superadmin"><AdminPage /></RequireRole>} />
        <Route path="/store/:slug" element={<StorePage />} />
        <Route path="/seller/:userId" element={<SellerProfilePage />} />
        <Route path="/profile" element={<RequireAuth><ProfilePage /></RequireAuth>} />
        <Route path="/certificate/:orderId" element={<RequireAuth><CertificatePage /></RequireAuth>} />
        <Route path="/go/:code" element={<AffiliatePage />} />
        <Route path="/ebook/:ebookId" element={<EbookRedirect />} />
        <Route path="/affiliate" element={<RequireAuth><AffiliateDashboardPage /></RequireAuth>} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <BottomNav />
    </BrowserRouter>
  );
}
